import React, { useMemo } from 'react';
import Typography from 'antd/lib/typography';
import Tooltip from 'antd/lib/tooltip';
import CheckCircleFilled from '@ant-design/icons/lib/icons/CheckCircleFilled';
import CheckCircleOutlined from '@ant-design/icons/lib/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/lib/icons/CloseCircleOutlined';
import EditOutlined from '@ant-design/icons/lib/icons/EditOutlined';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import { CustomizableColumnType } from 'src/components/table/CustomizableTable';
import { ActionList, IAction } from 'src/components/table/actionList/actionList';
import { PRIMARY_COLOR, DISABLED_COLOR } from 'src/theme';
import { defaultDateFormat } from 'src/domain/common/dateFormatters';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { ICustomerUtilityBill, displayRateClass, isBillEditable, isBillVerifiable } from 'src/domain/customer-utility-bill/interface';
import { BillStatusTag } from 'src/components/customer-utility-bill/BillStatusTag';
import { EMPTY_VALUE } from 'src/components/customer-utility-bill/billStyles';
import { FileIdCell } from 'src/components/customer-utility-bill/FileIdCell';
import type { ISorterState } from './useCustomerUtilityBillPageState';
import { formatCustomerName } from '../../../domain/customer-utility-bill-file/interface';

const { Text } = Typography;

// Greyed-out icon colour for an action that isn't available for the row (the action stays visible but
// disabled-looking, with a tooltip explaining why).
const DISABLED_ICON_COLOR = DISABLED_COLOR;

// Date-only display for billing period (date-fns); mirrors the modal's date pickers ("MMM D, YYYY" in dayjs).
const BILL_DATE_FORMAT = 'MMM d, yyyy';

// Composite/aliased table column key -> backend sort field.
export const SORT_FIELD_BY_KEY: Record<string, string> = { period: 'billingPeriodStart' };

const dash = <Text type="secondary">{EMPTY_VALUE}</Text>;

interface IUseBillColumnsParams {
    sorter: ISorterState;
    onEdit: (row: ICustomerUtilityBill) => void;
    onVerify: (row: ICustomerUtilityBill) => void;
    onUnverify: (row: ICustomerUtilityBill) => void;
    onAudit: (row: ICustomerUtilityBill) => void;
}

export const useBillColumns = ({
    sorter,
    onEdit,
    onVerify,
    onUnverify,
    onAudit,
}: IUseBillColumnsParams): CustomizableColumnType<ICustomerUtilityBill>[] =>
    useMemo(() => {
        const columns: CustomizableColumnType<ICustomerUtilityBill>[] = [
            // ─── 1. Pulled from Capturis CSV ─────────────────────────────────────
            {
                title: 'Location Name',
                dataIndex: 'siteName',
                key: 'siteName',
                width: 180,
                render: v => (v ? <Text strong>{v}</Text> : dash),
                sorter: true,
            },
            { title: 'Site/Store Number', dataIndex: 'siteNumber', key: 'siteNumber', width: 130, render: v => v || dash, sorter: true },
            {
                title: 'Billing Service Address',
                dataIndex: 'address',
                key: 'address',
                width: 220,
                render: v => v || dash,
                sorter: true,
            },
            { title: 'City', dataIndex: 'city', key: 'city', width: 130, render: v => v || dash, sorter: true },
            { title: 'State', dataIndex: 'state', key: 'state', width: 80, render: v => v || dash, sorter: true },
            { title: 'Vendor Name', dataIndex: 'vendor', key: 'vendor', width: 170, render: v => v || dash, sorter: true },
            { title: 'Utility Account Number', dataIndex: 'accountNumber', key: 'accountNumber', width: 170, render: v => v || dash, sorter: true },
            { title: 'Meter Number', dataIndex: 'meterNumber', key: 'meterNumber', width: 130, render: v => v || dash, sorter: true },
            { title: 'Enrollment ID', dataIndex: 'enrollmentId', key: 'enrollmentId', width: 140, render: v => v || dash, sorter: true },
            {
                title: 'Rate Class',
                dataIndex: 'rateClass',
                key: 'rateClass',
                width: 120,
                render: v => (displayRateClass(v) ? <Text>{v}</Text> : dash),
                sorter: true,
            },
            // ─── 2. Parsed from the bill PDF (AI-extracted) ──────────────────────
            { title: 'Zip Code', dataIndex: 'zipCode', key: 'zipCode', width: 120, render: v => v || dash, sorter: true },
            {
                title: 'Billing kW',
                dataIndex: 'billingKw',
                key: 'billingKw',
                width: 100,
                align: 'right',
                render: v => (v == null ? dash : <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</Text>),
                sorter: true,
            },
            {
                title: 'Billing kWh',
                dataIndex: 'billingKwh',
                key: 'billingKwh',
                width: 110,
                align: 'right',
                render: v => (v == null ? dash : <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</Text>),
                sorter: true,
            },
            {
                title: 'Billing Period',
                key: 'period',
                width: 190,
                render: (_, r) =>
                    r.billingPeriodStart ? (
                        <Text>
                            {defaultDateFormat(new Date(r.billingPeriodStart), EMPTY_VALUE, BILL_DATE_FORMAT)} →{' '}
                            {r.billingPeriodEnd ? defaultDateFormat(new Date(r.billingPeriodEnd), EMPTY_VALUE, BILL_DATE_FORMAT) : EMPTY_VALUE}
                        </Text>
                    ) : (
                        dash
                    ),
                sorter: true,
            },
            // ─── 3. DRMS-only fields ─────────────────────────────────────────────
            {
                title: 'Customer',
                dataIndex: 'customerName',
                key: 'customerName',
                width: 170,
                render: v => formatCustomerName(v) || dash,
                sorter: true,
            },
            { title: 'Market', dataIndex: 'market', key: 'market', width: 100, render: v => v || dash, sorter: true },
            { title: 'Zone', dataIndex: 'zone', key: 'zone', width: 100, render: v => v || dash, sorter: true },
            { title: 'Utility', dataIndex: 'utility', key: 'utility', width: 180, render: v => v || dash, sorter: true },
            {
                title: 'Control Provider',
                dataIndex: 'controlProviderName',
                key: 'controlProviderName',
                width: 170,
                render: v => v || dash,
                sorter: true,
            },
            { title: 'File ID', dataIndex: 'fileId', key: 'fileId', width: 140, render: v => <FileIdCell fileId={v} /> },
            {
                title: 'Status',
                dataIndex: 'processingStatus',
                key: 'processingStatus',
                width: 170,
                render: (_, r) => <BillStatusTag status={r.processingStatus} />,
                sorter: true,
            },
            {
                title: 'Verified',
                dataIndex: 'verifiedAt',
                key: 'verifiedAt',
                width: 120,
                align: 'center',
                render: v =>
                    v ? (
                        <Tooltip title={`Verified ${getComputerFormattedTime(v)}`}>
                            <CheckCircleFilled style={{ color: PRIMARY_COLOR, fontSize: 16 }} />
                        </Tooltip>
                    ) : (
                        dash
                    ),
                sorter: true,
            },
            {
                title: 'Updated At',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                width: 160,
                render: v => <Text>{getComputerFormattedTime(v)}</Text>,
                sorter: true,
            },
            // ─── 4. Row actions ──────────────────────────────────────────────────
            {
                title: 'Actions',
                key: 'actions',
                width: 90,
                hideable: false,
                render: (_, r) => {
                    const actions: IAction[] = [
                        {
                            // Any record can be opened; saving is gated inside the modal (only editable statuses can save).
                            title: (row: ICustomerUtilityBill) => (isBillEditable(row) ? 'Edit' : 'View (read-only)'),
                            icon: <EditOutlined />,
                            onClick: (row: ICustomerUtilityBill) => () => onEdit(row),
                        },
                        {
                            // Verified rows can always be un-verified; otherwise verify is offered only for records
                            // that carry reviewable data (SUCCESS / NEEDS_REVIEW).
                            title: (row: ICustomerUtilityBill) =>
                                row.verifiedAt
                                    ? 'Mark as unverified'
                                    : isBillVerifiable(row)
                                      ? 'Mark as verified'
                                      : 'Only parsed bills with data can be verified',
                            icon: (row: ICustomerUtilityBill) =>
                                row.verifiedAt ? (
                                    <CloseCircleOutlined />
                                ) : (
                                    <CheckCircleOutlined style={{ color: isBillVerifiable(row) ? 'inherit' : DISABLED_ICON_COLOR }} />
                                ),
                            onClick: (row: ICustomerUtilityBill) =>
                                row.verifiedAt ? () => onUnverify(row) : isBillVerifiable(row) ? () => onVerify(row) : () => undefined,
                        },
                        { title: 'Audit trail', icon: <HistoryOutlined />, onClick: (row: ICustomerUtilityBill) => () => onAudit(row) },
                    ];
                    return <ActionList actions={actions} item={r} alwaysShow />;
                },
            },
        ];

        // Reflect the active URL sort on the column header (sorting is server-side / controlled order).
        return columns.map(col => {
            if (!col.sorter) return col;
            const field = SORT_FIELD_BY_KEY[String(col.key)] ?? String(col.key);
            return { ...col, sortOrder: sorter.field === field ? (sorter.order ?? null) : null };
        });
    }, [sorter, onEdit, onVerify, onUnverify, onAudit]);
